import { isDate, parseISO } from 'date-fns';
import Hashids from 'hashids/esm/hashids';
import { Observable, throwError, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { TeamMember } from '@shared/models';

export const getObjectKeys = (obj: any) =>
  typeof obj?.keys === 'function' ? obj.keys() : Object.keys(obj);

export const getPropsArray = (obj: {}) => getObjectKeys(obj).map((key) => obj[key]);

export const getPropsArrayWithKeys = (obj: {}, prop: string = '_key') =>
  getObjectKeys(obj).map((key) => ({ ...obj[key], [prop]: key }));

export const getObjectFromArray = (arr) =>
  arr.reduce((result, item) => {
    const key = Object.keys(item)[0];
    return { ...result, [key]: item[key] };
  });
export const flatArray = (arr) => arr.reduce((a, b) => a.concat(b));

export const groupBy = <T, K>(list: T[] = [], getter: (item: T) => K): Map<K, [T]> => {
  const group = new Map<K, [T]>();
  list.forEach((item: T) => {
    const key = getter(item);
    if (!group.has(key)) {
      group.set(key, [item]);
    } else {
      group.get(key).push(item);
    }
  });
  return group;
};

export const removeEmpty = (obj = {}) =>
  Object.keys(obj)
    .filter(
      (k) =>
        (obj[k] !== null && obj[k] !== undefined) ||
        (Array.isArray(obj[k]) && Array.from(obj[k]).length !== 0),
    )
    .reduce(
      (newObj, k) =>
        typeof obj[k] === 'object' && !isDate(obj[k]) && !Array.isArray(obj[k])
          ? Object.assign(newObj, { [k]: removeEmpty(obj[k]) })
          : Object.assign(newObj, { [k]: obj[k] }),
      {},
    );

// TODO: this one is breaking on some input that doesn't extend Object
// and has missing methods like get keys and others mostly noticed on some
// itercepted request params
export const removeEmptyFromMap = (obj) =>
  getObjectKeys(obj)
    .filter((k) => obj.get(k) !== null && obj.get(k) !== undefined)
    .reduce(
      (newObj, k) =>
        typeof obj.get(k) === 'object' && !isDate(obj.get(k))
          ? Object.assign(newObj, { [k]: removeEmptyFromMap(obj.get(k)) })
          : Object.assign(newObj, { [k]: obj.get(k) }),
      {},
    );

export const checkDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export const dateReviver = (key, value) => (checkDate.test(value) ? parseISO(value) : value);

export const copyObject = (obj) => JSON.parse(JSON.stringify(removeEmpty(obj)), dateReviver);

export const listByProperty = (array, property: string): any[] =>
  Array.from(new Set(array.filter((item) => !!item).map((item) => item[property])));

export interface Trackable<T = number> {
  id: T;
  lastModifiedDate: string;
}

export const trackElement = (index: number, item: Trackable) =>
  `${item.id} ${item.lastModifiedDate}`;

export const capitalize = (val: string) =>
  !!val ? val.toLocaleLowerCase().replace(/(?:^|\s)\S/g, (a) => a.toUpperCase()) : '';

export function getInitials(name: string): string {
  return name
    ? !!name &&
        name
          .split(/\s/)
          .map((word: string) => word.charAt(0))
          .slice(0, 2)
          .join('')
          .toUpperCase()
    : '';
}

export function getInitialsFromTeamMember(teamMember: TeamMember): string {
  if (teamMember && teamMember.firstName && teamMember.lastName) {
    return `${teamMember.firstName.charAt(0).toUpperCase()}${teamMember.lastName
      .charAt(0)
      .toUpperCase()}`;
  }
  return teamMember
    ? !!teamMember &&
        teamMember.name &&
        teamMember.name
          .split(/\s/)
          .map((word: string) => word.charAt(0))
          .slice(0, 2)
          .join('')
          .toUpperCase()
    : '';
}

export function getVisibleToTeamMembersText(teamMembers: TeamMember[]): string {
  if (!teamMembers) {
    return '';
  }
  return (
    'Visible to: ' +
    teamMembers
      .map((teamMember) => getTeamMemberName(teamMember))
      .filter((v) => v !== '')
      .join(', ')
  );
}

export function getTeamMemberName(teamMember: TeamMember): string {
  return teamMember.name ?? (teamMember.firstName && teamMember.lastName)
    ? `${teamMember.firstName} ${teamMember.lastName}`
    : teamMember.firstName || teamMember.lastName || '';
}

export function toInteger(value: any): number {
  return parseInt(value, 10);
}

export function isNumber(value: any): value is number {
  return /^\d+$/.test(value);
}

export function normalizeTimestamp(value: string): number {
  return value.length === 10 ? parseInt(value, 10) * 1000 : parseInt(value, 10);
}

export function parseDate(value: string): Date {
  return isNumber(value) ? new Date(normalizeTimestamp(value)) : parseISO(value);
}

export const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export const slugify = (text: string): string =>
  !!text &&
  text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '');

export const validUrl = /^((http|https):\/\/)?(www\.)?([A-z]+)\.([A-z]{2,})/;

export const pdfFile = /^.*\.pdf$/i;

// TODO remove this method and setup reducer's initial state
//  through effects lifecycle actions using LocalStorageService
export const retrieve = (key: string, initialState: object = {}) => ({
  ...initialState,
  ...(localStorage.hasOwnProperty(key) ? JSON.parse(localStorage.getItem(key)) : {}),
});

export function isTruthy<T>(input: T): input is T {
  return !!input;
}

export function createAnonymizedName(id: number, initials: string): string {
  return `${initials}-${new Hashids('mojo', 6).encode(id)}`;
}

export const capitalLetters = /\b\w/g;

export const capitalizeInitials = (input: string) => input.match(capitalLetters).join('');
export function enumToArray(enumme): any[] {
  return Object.keys(enumme).map((key) => enumme[key]);
}
export function removeDuplicateSpaces(value: string): string {
  return value ? value.replace(/ +(?= )/g, '').trim() : value;
}

export function generateRandomString(
  length: number,
  { wordMinLength = 2, wordMaxLength = 15 } = {},
): string {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let lastWordEnd = 0;
  for (let i = 0; i < length; i++) {
    if (
      i - lastWordEnd > wordMinLength + Math.random() * (wordMaxLength - wordMinLength) &&
      i < length - wordMinLength - 1
    ) {
      result += ' ';
      lastWordEnd = i;
    } else {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
  }
  return result;
}

export function unique(array, attribute: string): never[] {
  return Array.from(new Set(array.filter((job) => job[attribute]).map((job) => job[attribute])));
}

export const genericRetryStrategy =
  ({
    maxRetryAttempts = 3,
    scalingDuration = 1000,
    excludedStatusCodes = [],
  }: {
    maxRetryAttempts?: number;
    scalingDuration?: number;
    excludedStatusCodes?: number[];
  } = {}) =>
  (attempts: Observable<any>) =>
    attempts.pipe(
      mergeMap((error, i) => {
        const retryAttempt = i + 1;

        if (
          retryAttempt > maxRetryAttempts ||
          excludedStatusCodes.find((e) => e === error.status)
        ) {
          return throwError(error);
        }

        return timer(retryAttempt * scalingDuration);
      }),
    );

export function selectByValue<T extends { value: string }>(value: string, options: T[]): T {
  return options.find((item) => item.value === value);
}

export const warningPercentage = (maxLength: number, percentage = 80) =>
  maxLength - ((100 - percentage) / 100) * maxLength;

export const deeperCompare = (a, b): boolean => JSON.stringify(a) === JSON.stringify(b);

export const WAITING_FOR_APPLICATION_STATUS = 'WAITING_FOR_APPLICATION';
export const PROSPECT_STATUS = 'PROSPECT';
export const PROSPECTS_LABEL = 'Prospects';
export const hostname = (): string => location.hostname;

export const isLocal = (): boolean => hostname() === 'localhost' || hostname() === '127.0.0.1';

export const tenantNameFromPath = (defaultTenant?: string): string =>
  isLocal() ? defaultTenant : hostname().split('.')[0];

export const toTenantUrl = (url: string, defaultTenant?: string): string =>
  !!url ? url.replace(/TENANT/, tenantNameFromPath(defaultTenant)) : undefined;

export const getDate = (date: Date) =>
  new Date(date.getTime() + Math.abs(date.getTimezoneOffset() * 60000));

export const getFormat = (date: string) => {
  let dateFormated = '';

  date.split('/').forEach((item, index) => {
    dateFormated += item.length === 1 ? `0${item}` : `${item}`;

    if (index < date.split('/').length - 1) {
      dateFormated += '/';
    }
  });

  return dateFormated;
};
